import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useShowAgentsInNav } from "src/components/layout/nav/sections/hooks/agents";
import { useLastSettingsPath } from "src/components/layout/nav/sections/hooks/last-settings-path";
import { useProjectsAccess } from "src/components/layout/nav/sections/hooks/projects";
import { useSpaceContext } from "src/components/spaces";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { AGENT_SETTINGS_TABS } from "src/pages/agents/settings/tabs";
import { PROJECTS_PATHS } from "src/pages/projects/constants";
import { useWorkspaceSettingsTabs } from "src/pages/settings/tabs";

const WORKSPACE_SETTINGS_PATH = "/settings";

/**
 * Where the nav's Settings opens: back on the settings page the viewer last had open, while the sidebar still lists it.
 * Otherwise the first workspace settings page.
 * A space that hides workspace settings opens on the first other settings page it allows, and null means it allows none.
 */
export const useSettingsHomePath = (): string | null => {
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const { isEnabled: hasProjects } = useProjectsAccess();
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const { hasAccess: hasOrganizationSettings } = useHasOrganizationSettings();
  const {
    isInSpace,
    isPathAccessibleInSpace,
    isPathOrChildrenAccessibleInSpace,
  } = useSpaceContext();
  const workspaceTabs = useWorkspaceSettingsTabs().filter(
    (tab) => !tab.isHidden,
  );
  const firstWorkspaceTab = workspaceTabs[0];
  const lastSettingsPath = useLastSettingsPath();

  const agentSettingsPaths =
    showAgents && isAgentsEnabled && canConfigureAgents
      ? AGENT_SETTINGS_TABS.map((tab) => `/agents/settings/${tab.path}`)
      : [];
  const workspacePath = firstWorkspaceTab
    ? `${WORKSPACE_SETTINGS_PATH}/${firstWorkspaceTab.path}`
    : WORKSPACE_SETTINGS_PATH;
  const candidates = [
    workspacePath,
    hasProjects && PROJECTS_PATHS.settings,
    agentSettingsPaths[0],
    hasOrganizationSettings && "/organization",
  ];

  // Workspace settings follow the settings sidebar's gate, which a space opens or closes as a whole.
  const isAccessible = (path: string) =>
    !isInSpace ||
    (path.startsWith(WORKSPACE_SETTINGS_PATH)
      ? isPathAccessibleInSpace(WORKSPACE_SETTINGS_PATH)
      : isPathOrChildrenAccessibleInSpace(path));

  // The settings sidebar's pages, under its gates, so a remembered page the viewer has since lost is skipped.
  const sidebarPaths = [
    ...workspaceTabs.map((tab) => `${WORKSPACE_SETTINGS_PATH}/${tab.path}`),
    hasProjects && PROJECTS_PATHS.settings,
    // The custom agents sit under Context.
    ...agentSettingsPaths,
    hasOrganizationSettings && "/organization",
  ];
  const lastPathname = lastSettingsPath?.split(/[?#]/)[0];
  const isLastPathAvailable =
    lastPathname !== undefined &&
    sidebarPaths.some(
      (path) =>
        typeof path === "string" &&
        (lastPathname === path || lastPathname.startsWith(`${path}/`)) &&
        isAccessible(path),
    );
  if (lastSettingsPath && isLastPathAvailable) return lastSettingsPath;

  return (
    candidates.find(
      (path): path is string => typeof path === "string" && isAccessible(path),
    ) ?? null
  );
};
